import React, { type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from 'react';
import type { LinkProps } from 'react-router-dom';

import Badge from '#@/components/Badge';
import Icon, { type IconProps } from '#@/components/Icon';
import { BUTTON_VARIANT } from '#@/shared/constants';
import { COLOR } from '#@/shared/utils/styles';

import { StyledButton, StyledSpinner } from './Styles';
import type { ButtonVariantV2 } from './types';
import {
  getBadgeColorFromButtonVariant,
  getColorFromButtonVariant,
  getIconConfigurationFromOldProps,
  getVariantV2,
  isInverted,
  isSecondary,
} from './utils';

interface IconConfiguration {
  type: IconProps['type'];
  size?: IconProps['size'];
  color?: COLOR;
}

export interface ButtonProps {
  forwardRef?: React.Ref<HTMLButtonElement | HTMLAnchorElement>;
  className?: string;
  children?: ReactNode;
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  /** @deprecated
   * Use `variant` instead.
   */
  hollow?: boolean;
  variant?: BUTTON_VARIANT | ButtonVariantV2;
  /** @deprecated
   *  Use `iconLeft`, `iconRight` or `onlyIcon` instead.
   */
  icon?: IconProps['type'];
  /** @deprecated
   *  Use `iconLeft` and `iconRight` instead.
   */
  iconColor?: COLOR;
  /** @deprecated
   *  Use `iconLeft` and `iconRight` instead.
   */
  iconSize?: IconProps['size'];
  /** @deprecated
   *  Use `iconLeft` and `iconRight` instead.
   */
  iconPosition?: 'right' | 'left';
  iconLeft?: IconConfiguration;
  iconRight?: IconConfiguration;
  onlyIcon?: IconConfiguration;
  disabled?: boolean;
  visuallyDisabled?: boolean;
  working?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  linkTo?: LinkProps['to'];
  linkToExternal?: string;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  style?: ButtonHTMLAttributes<HTMLButtonElement>['style'];
  badgeCount?: number;
  badgeColor?: COLOR;
  /** Set when button is used as a trigger, e.g. in `DropdownMenu`. Applies active styles. */
  isTriggered?: boolean;
  align?: 'flex-start' | 'center' | 'flex-end';
}

const Button: React.FC<ButtonProps & ButtonHTMLAttributes<HTMLButtonElement>> = ({
  forwardRef,
  className,
  children,
  type = 'button',
  hollow = false,
  variant: variantOld = BUTTON_VARIANT.primary,
  icon,
  iconColor,
  iconSize,
  iconPosition = 'left',
  iconLeft: propsIconLeft,
  iconRight: propsIconRight,
  onlyIcon,
  disabled = false,
  visuallyDisabled = disabled,
  working = false,
  onClick,
  linkTo,
  linkToExternal,
  size,
  fullWidth = false,
  style,
  badgeCount,
  badgeColor: customBadgeColor,
  isTriggered,
  align = 'center',
  ...otherProps
}) => {
  const variant = Object.values(BUTTON_VARIANT).includes(variantOld as BUTTON_VARIANT)
    ? getVariantV2(variantOld as BUTTON_VARIANT, hollow)
    : (variantOld as ButtonVariantV2);
  const buttonColor = getColorFromButtonVariant(variant);
  const spinnerColor = isSecondary(variant) || isInverted(variant) ? 'inherit' : COLOR.white;
  const badgeColor = customBadgeColor || getBadgeColorFromButtonVariant(variant);
  const { iconLeft, iconRight } = {
    iconLeft: propsIconLeft,
    iconRight: propsIconRight,
    ...(icon && getIconConfigurationFromOldProps({ icon, iconColor, iconSize, iconPosition })),
  };

  return (
    <StyledButton
      // This data-attribute is used by `ButtonGroup`
      data-button-alignable-with-text={isSecondary(variant)}
      forwardRef={forwardRef}
      className={className}
      type={type}
      buttonColor={buttonColor}
      disabled={disabled || working}
      $visuallyDisabled={disabled || visuallyDisabled}
      {...(visuallyDisabled && { 'data-visually-disabled': true })}
      working={working}
      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
        if (!disabled && !working && onClick) {
          onClick(event);
        }
      }}
      hasLeftIcon={Boolean(iconLeft)}
      hasRightIcon={Boolean(iconRight)}
      iconOnly={!children && badgeCount === undefined}
      linkTo={linkTo}
      linkToExternal={linkToExternal}
      small={size === 'small'}
      large={size === 'large'}
      fullWidth={fullWidth}
      variant={variant}
      style={style}
      isTriggered={isTriggered ?? false}
      align={align}
      {...otherProps}
    >
      {!working && onlyIcon && (
        <Icon type={onlyIcon.type} size={onlyIcon.size} color={onlyIcon.color} />
      )}
      {working && <StyledSpinner size={22} color={spinnerColor} data-is-left-icon />}
      {!working && iconLeft && (
        <Icon data-is-left-icon type={iconLeft.type} size={iconLeft.size} color={iconLeft.color} />
      )}
      {children}
      {badgeCount !== undefined && <Badge color={badgeColor} count={badgeCount} />}
      {!working && iconRight && (
        <Icon
          data-is-right-icon
          type={iconRight.type}
          size={iconRight.size}
          color={iconRight.color}
        />
      )}
    </StyledButton>
  );
};

export default Button;
